/**
 * useGateAction Hook - Single source of truth for gated actions
 * 
 * Key principles:
 * - If isUnlocked: deliver content immediately (download/redirect/scroll), show toast, DO NOT open modal
 * - If !isUnlocked: open registration modal
 * 
 * This prevents the "invisible modal" bug where modal opens but renders nothing for unlocked users.
 */

import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLeadGate } from '@/contexts/LeadGateContext';
import { trackEvent } from '@/lib/analytics';
import { toast } from '@/hooks/use-toast';
import { projectData } from '@/data/project';

export type GateChannel = 'whatsapp' | 'call' | 'email' | 'form';

export interface GateActionConfig {
  cta: string;
  source: string;
  modalType: string;
  includeGoldCampaign?: boolean;
  downloadUrl?: string;
  downloadName?: string;
  onSuccess?: () => void;
  // Optional: force a specific channel (e.g., always open WhatsApp)
  forceChannel?: GateChannel;
  // Optional: WhatsApp message template
  whatsappMessageTemplate?: string;
  // Optional: redirect to this route when unlocked
  redirectTo?: string;
  // Optional: scroll to this element ID when unlocked
  scrollToId?: string;
}

export interface UseGateActionReturn {
  run: () => void;
  modalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  modalType: string;
  isAlreadyUnlocked: boolean;
  source: string;
}

export function useGateAction(config: GateActionConfig): UseGateActionReturn {
  const location = useLocation();
  const navigate = useNavigate();
  const { isUnlocked } = useLeadGate();
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  // Helper to trigger file download
  const triggerDownload = useCallback((url: string, name?: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name || 'download';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const run = useCallback(() => {
    // Track the CTA click regardless of unlock status
    trackEvent('cta_click', {
      cta: config.cta,
      source: config.source,
      modalType: config.modalType,
      page: location.pathname,
      isUnlocked,
    });

    // Track gate opened event when modal will open
    const trackGateOpened = () => {
      trackEvent('gate_opened', {
        source: config.source,
        modalType: config.modalType,
        page: location.pathname,
      });
    };

    // If forceChannel is whatsapp, open WhatsApp directly (bypass gate entirely)
    if (config.forceChannel === 'whatsapp') {
      const message = config.whatsappMessageTemplate || 'Hi, I am interested in Nahar Chandi Valley.';
      const whatsappUrl = `https://wa.me/${projectData.contact.whatsappNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      return;
    }

    // UNLOCKED USERS: Deliver content immediately, DO NOT open modal
    // Priority order: redirect → scroll → download → fallback (ONE toast only)
    if (isUnlocked) {
      // Track gate bypass for unlocked users
      trackEvent('gate_bypassed_unlocked', {
        source: config.source,
        modalType: config.modalType,
        page: location.pathname,
        action: config.redirectTo ? 'redirect' : config.scrollToId ? 'scroll' : config.downloadUrl ? 'download' : 'fallback',
      });

      // Determine if this is a pricing-related action for contextual messaging
      const isPricingAction = 
        config.modalType === 'pricing' || 
        config.modalType === 'price' ||
        config.redirectTo?.includes('price') ||
        config.redirectTo?.includes('floor-plan') ||
        config.cta.toLowerCase().includes('pricing') ||
        config.cta.toLowerCase().includes('floor plan');

      // 1. Priority: Handle redirects (user expects navigation)
      if (config.redirectTo) {
        toast({
          title: "Access confirmed",
          description: isPricingAction 
            ? "Opening pricing & floor plans…" 
            : "Opening your content…",
        });
        navigate(config.redirectTo);
        config.onSuccess?.();
        return;
      }
      
      // 2. Handle scroll to section
      if (config.scrollToId) {
        const element = document.getElementById(config.scrollToId);
        if (element) {
          toast({
            title: "Access confirmed",
            description: isPricingAction 
              ? "Opening pricing & floor plans…" 
              : "Scrolling to content…",
          });
          element.scrollIntoView({ behavior: 'smooth' });
          config.onSuccess?.();
          return;
        }
      }

      // 3. Handle downloads
      if (config.downloadUrl) {
        toast({
          title: "Download started",
          description: config.downloadName?.toLowerCase().includes('floor') 
            ? "Your floor plans are downloading." 
            : "Your file is downloading. Check your downloads folder.",
        });
        triggerDownload(config.downloadUrl, config.downloadName);
        config.onSuccess?.();
        return;
      }

      // 4. Fallback: Show generic confirmation if no specific action
      toast({
        title: "You have full access",
        description: isPricingAction 
          ? "You can view pricing & floor plans anytime."
          : "Our team will contact you shortly with pricing and floor plan details.",
      });
      config.onSuccess?.();
      
      // CRITICAL: Do NOT open modal for unlocked users
      return;
    }

    // LOCKED USERS: Open registration modal
    setModalOpen(true);
  }, [config, location.pathname, isUnlocked, navigate, triggerDownload]);

  return {
    run,
    modalOpen,
    openModal,
    closeModal,
    modalType: config.modalType,
    isAlreadyUnlocked: isUnlocked,
    source: config.source,
  };
}
